import { createAsyncThunk } from '@reduxjs/toolkit';

import axiosInstance from 'utils/axios';
import type {
    CreateUnitFloorRoomMappingPayload,
    FetchHospitalUnitOptionsParams,
    FetchUnitFloorRoomMappingsParams,
    FetchUnitFloorRoomMappingsApiResponse,
    HospitalUnitOption,
    UnitFloorRoomMappingApiItem,
    UnitFloorRoomMapping,
    UpdateUnitFloorRoomMappingPayload
} from '@/types/unit-floor-room-mapping';
import { mapUnitFloorRoomMappingItem } from '@/utils/unit-floor-room-mapping/map-unit-floor-room-mapping-item';
import { getErrorMessage } from 'utils/error-message';

export const fetchHospitalUnitOptions = createAsyncThunk<HospitalUnitOption[], FetchHospitalUnitOptionsParams, { rejectValue: string }>(
    'unitFloorRoomMapping/fetchHospitalUnitOptions',
    async (params) => {
        try {
            const hasMultipleHospitals = Array.isArray(params.hospitalIds) && params.hospitalIds.length > 0;
            const { data } = hasMultipleHospitals
                ? await axiosInstance.post<{ items?: HospitalUnitOption[] }>('/admin/units/multi-hospital', {
                      hospitalIds: params.hospitalIds
                  })
                : await axiosInstance.get<HospitalUnitOption[] | { items?: HospitalUnitOption[] }>('/admin/units', {
                      params
                  });

            if (Array.isArray(data)) {
                return data;
            }

            if (Array.isArray(data?.items)) {
                return data.items;
            }

            // Keep local flow usable if API responds with an unexpected shape.
            return [];
        } catch {
            return [];
        }
    }
);

export const fetchUnitFloorRoomMappings = createAsyncThunk<
    {
        items: UnitFloorRoomMapping[];
        total: number;
        page: number;
        limit: number;
    },
    FetchUnitFloorRoomMappingsParams,
    { rejectValue: string }
>('unitFloorRoomMapping/fetchMappings', async (params, { rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.get<FetchUnitFloorRoomMappingsApiResponse>('/admin/mapping/unit-floor-room', {
            params: {
                hospitalId: params.hospitalId,
                page: params.page,
                limit: params.limit,
                search: params.search || undefined,
                sortBy: params.sortBy || undefined,
                sortOrder: params.sortOrder || undefined
            }
        });

        const mappedItems = (data?.items ?? []).map(mapUnitFloorRoomMappingItem);

        return {
            items: mappedItems,
            total: data?.total ?? mappedItems.length,
            page: data?.page ?? params.page ?? 1,
            limit: data?.limit ?? params.limit ?? 10
        };
    } catch (error: unknown) {
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch mappings'));
    }
});

export const createUnitFloorRoomMapping = createAsyncThunk<
    UnitFloorRoomMapping,
    CreateUnitFloorRoomMappingPayload,
    { rejectValue: string }
>('unitFloorRoomMapping/createMapping', async (payload, { rejectWithValue, getState }) => {
    try {
        const { data } = await axiosInstance.post<UnitFloorRoomMappingApiItem>('/admin/mapping/unit-floor-room', payload);
        return mapUnitFloorRoomMappingItem(data);
    } catch (error: unknown) {
        return rejectWithValue(getErrorMessage(error, 'Failed to create unit mapping'));
    }
});

export const updateUnitFloorRoomMapping = createAsyncThunk<
    UnitFloorRoomMapping,
    UpdateUnitFloorRoomMappingPayload,
    { rejectValue: string }
>('unitFloorRoomMapping/updateMapping', async (payload, { rejectWithValue, getState }) => {
    try {
        const { id, ...body } = payload;
        await axiosInstance.patch<UnitFloorRoomMappingApiItem>(`/admin/mapping/unit-floor-room/${id}`, body);
        return mapUnitFloorRoomMappingItem({ ...payload, hospitalId: '', unitId: payload.unitId ?? '' });
    } catch (error: unknown) {
        return rejectWithValue(getErrorMessage(error, 'Failed to update unit mapping'));
    }
});
